/**
 * Auth Hook & Context
 * CCH Care - Version 1.0
 * 
 * Manages authentication state:
 * - Login flow (credentials → 2FA → session)
 * - Session validation on app load
 * - Token refresh on session.php response
 * - Logout with cleanup
 * - Facility selection
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, verify2FA as apiVerify2FA, validateSession, logout as apiLogout } from '../../api/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Validate existing session
   */
  const checkSession = useCallback(async () => {
    try {
      setLoading(true);
      const data = await validateSession();
      
      if (data.valid) {
        // Update token (session.php returns refreshed token)
        if (data.token) {
          localStorage.setItem('care_token', data.token);
        }
        
        setUser(data.user);
        setMemberships(data.memberships || []);
        setFacilities(data.facilities || []);
        setPermissions(data.permissions || []);
      } else {
        clearAuth();
      }
    } catch (e) {
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check for existing session on mount
   */
  useEffect(() => {
    const token = localStorage.getItem('care_token');
    if (token) {
      checkSession();
    } else {
      setLoading(false);
    }
  }, [checkSession]);

  /**
   * Login — Step 1
   */
  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const data = await apiLogin(email, password);
      return data; // Caller handles 2FA flow
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Login failed';
      setError(msg);
      throw e;
    }
  }, []);

  /**
   * Verify 2FA — Step 2
   */
  const verify2FA = useCallback(async (tempToken, code) => {
    setError(null);
    try {
      const data = await apiVerify2FA(tempToken, code);
      
      // Store auth data
      localStorage.setItem('care_token', data.token);
      
      const defaultMembership = data.memberships[0];
      localStorage.setItem('care_membership_id', defaultMembership.membership_id);
      localStorage.setItem('care_tenant_id', defaultMembership.tenant_id);
      
      if (data.facilities?.length > 0) {
        localStorage.setItem('care_facility_id', data.facilities[0].facility_id);
      }
      
      setUser(data.user);
      setMemberships(data.memberships);
      setFacilities(data.facilities || []);
      
      return data;
    } catch (e) {
      const msg = e.response?.data?.error || e.message || '2FA verification failed';
      setError(msg);
      throw e;
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    await apiLogout();
    clearAuth();
  }, []);

  /**
   * Switch active facility
   */
  const switchFacility = useCallback((facilityId) => {
    localStorage.setItem('care_facility_id', facilityId);
    // Trigger re-render by updating state
    const facility = facilities.find(f => f.facility_id === facilityId);
    if (facility) {
      setUser(prev => ({ ...prev, active_facility_id: facilityId }));
    }
  }, [facilities]);

  /**
   * Switch active membership (for multi-tenant users)
   */
  const switchMembership = useCallback(async (membershipId) => {
    const membership = memberships.find(m => m.membership_id === membershipId);
    if (!membership) return;
    
    localStorage.setItem('care_membership_id', membershipId);
    localStorage.setItem('care_tenant_id', membership.tenant_id);
    
    // Re-validate session with new membership context
    await checkSession();
  }, [memberships, checkSession]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission) => {
    if (user?.is_tenant_admin) return true;
    return permissions.includes(permission);
  }, [user, permissions]);

  /**
   * Clear all auth state
   */
  function clearAuth() {
    localStorage.removeItem('care_token');
    localStorage.removeItem('care_membership_id');
    localStorage.removeItem('care_tenant_id');
    localStorage.removeItem('care_facility_id');
    setUser(null);
    setMemberships([]);
    setFacilities([]);
    setPermissions([]);
  }

  const value = {
    user,
    memberships,
    facilities,
    permissions,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    verify2FA,
    logout,
    switchFacility,
    switchMembership,
    hasPermission,
    checkSession,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;
